import * as Popover from '@radix-ui/react-popover'
import { StyleSheet, css } from 'aphrodite'
import type React from 'react'

interface DurationSelectorProps {
  value: string
  onChange: (duration: string) => void
  disabled?: boolean
  durations?: string[]
}

export const DurationSelector: React.FC<DurationSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  durations = ['15M', '30M', '45M', '1H', '2H', '3H', '4H'],
}) => {
  const handleDurationSelect = (duration: string) => {
    if (disabled) return

    const numericValue = duration.replace(/[MH]/, '')
    if (duration.endsWith('H')) {
      // Convert hours to minutes
      const minutes = Number.parseInt(numericValue) * 60
      onChange(value === String(minutes) ? '' : String(minutes))
    } else {
      onChange(value === numericValue ? '' : numericValue)
    }
  }

  const getDisplayValue = (value: string): string => {
    const minutes = Number.parseInt(value)
    if (minutes >= 60 && minutes % 60 === 0) {
      return `${minutes / 60}H`
    }
    return `${minutes}M`
  }

  return (
    <div className={css(styles.durationOptions)}>
      {durations.map((duration) => (
        <button
          key={duration}
          className={css(
            styles.durationButton,
            disabled ? styles.buttonDisabled : null,
            value ===
              (duration.endsWith('H')
                ? String(Number.parseInt(duration) * 60)
                : duration.replace('M', ''))
              ? styles.durationButtonSelected
              : null,
          )}
          onClick={() => handleDurationSelect(duration)}
          disabled={disabled}
        >
          <span className={css(styles.durationButtonText)}>{duration}</span>
        </button>
      ))}
    </div>
  )
}

const styles = StyleSheet.create({
  durationOptions: {
    display: 'flex',
    gap: '8px',
    width: '100%',
  },
  durationButton: {
    flex: 1,
    padding: '12px 0',
    borderRadius: '4px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
    color: '#374151',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'Inter',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  durationButtonSelected: {
    backgroundColor: '#1C64F2',
    color: '#FFFFFF',
    border: 'none',
    ':hover': {
      backgroundColor: '#1C64F2',
    },
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    backgroundColor: '#F3F4F6',
    borderColor: '#E5E7EB',
    color: '#6B7280',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  durationButtonText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '52px',
  },
})

export default DurationSelector
